/**
 * GlobalFooter — Massive "Fat Footer" optimised for SEO siloing & internal linking.
 * 4-column grid (Brand · IT · BPO · Industries & Company) + trust/legal bar.
 * Fully localised across 16 languages.
 */
import { useState, forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { startHref } from '@/lib/ctaSource';
import {
  Mail, Phone, MapPin, Twitter, Facebook, Instagram, Youtube,
  MessageCircle, Send, CheckCircle, Shield, Award, Lock, Globe,
  Code2, Server, Cloud, ShieldCheck, Users, Headphones, FileSearch,
  BarChart3, Database, Building2, Stethoscope, Landmark, ShoppingCart,
  GraduationCap, Factory, Plane, Gamepad2, Leaf, Truck, Zap
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { Separator } from '@/components/ui/separator';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import BRAND from '@/config/brand';
import { useSchemaDOM } from '@/hooks/useSchemaDOM';

/* ─── i18n helper ─── */
type L = Record<string, string>;
const tx = (map: L, lang: string): string => map[lang] || map.en;

/* ─── All footer labels — 16 languages ─── */
const getFooterLabels = (lang: string) => ({
  tagline: tx({
    en: 'Global IT & BPO Solutions', fr: 'Solutions IT & BPO Mondiales', es: 'Soluciones IT & BPO Globales', ar: 'حلول تكنولوجيا المعلومات والتعهيد العالمية',
    de: 'Globale IT & BPO Lösungen', it: 'Soluzioni IT & BPO Globali', pt: 'Soluções IT & BPO Globais', nl: 'Wereldwijde IT & BPO Oplossingen',
    pl: 'Globalne Rozwiązania IT & BPO', ru: 'Глобальные ИТ и BPO Решения', zh: '全球IT与BPO解决方案', ja: 'グローバルIT＆BPOソリューション',
    tr: 'Küresel IT & BPO Çözümleri', ko: '글로벌 IT & BPO 솔루션', sv: 'Globala IT & BPO-lösningar', da: 'Globale IT & BPO-løsninger',
    hi: 'वैश्विक IT और BPO समाधान', ro: 'Soluții IT & BPO Globale', cs: 'Globální IT & BPO řešení', hu: 'Globális IT & BPO megoldások', fi: 'Globaalit IT & BPO -ratkaisut',
  }, lang),
  taglineDesc: tx({
    en: 'Premium outsourcing & digital innovation from Casablanca. 24/7 multilingual teams powering your growth.',
    fr: 'Externalisation premium et innovation digitale depuis Casablanca. Équipes multilingues 24/7 au service de votre croissance.',
    es: 'Externalización premium e innovación digital desde Casablanca. Equipos multilingües 24/7 impulsando tu crecimiento.',
    ar: 'تعهيد متميز وابتكار رقمي من الدار البيضاء. فرق متعددة اللغات على مدار الساعة لتعزيز نموك.',
    de: 'Premium-Outsourcing & digitale Innovation aus Casablanca. 24/7 mehrsprachige Teams für Ihr Wachstum.',
    it: 'Outsourcing premium e innovazione digitale da Casablanca. Team multilingue 24/7 per la tua crescita.',
    pt: 'Outsourcing premium e inovação digital de Casablanca. Equipas multilingues 24/7 impulsionando o seu crescimento.',
    nl: 'Premium outsourcing & digitale innovatie vanuit Casablanca. 24/7 meertalige teams voor uw groei.',
    pl: 'Outsourcing premium i innowacje cyfrowe z Casablanki. Wielojęzyczne zespoły 24/7 wspierające Twój rozwój.',
    ru: 'Премиум-аутсорсинг и цифровые инновации из Касабланки. Многоязычные команды 24/7 для вашего роста.',
    zh: '来自卡萨布兰卡的优质外包与数字创新。全天候多语言团队助力您的增长。',
    ja: 'カサブランカからのプレミアムアウトソーシングとデジタルイノベーション。24時間365日多言語チームが成長を支援します。',
    tr: 'Kazablanka\'den premium dış kaynak ve dijital inovasyon. 7/24 çok dilli ekipler büyümenize güç katıyor.',
    ko: '카사블랑카에서 제공하는 프리미엄 아웃소싱과 디지털 혁신. 24/7 다국어 팀이 성장을 지원합니다.',
    sv: 'Premium outsourcing & digital innovation från Casablanca. 24/7 flerspråkiga team som driver din tillväxt.',
    da: 'Premium outsourcing & digital innovation fra Casablanca. 24/7 flersprogede teams der driver din vækst.',
    hi: 'कासाब्लांका से प्रीमियम आउटसोर्सिंग और डिजिटल इनोवेशन। 24/7 बहुभाषी टीमें आपकी वृद्धि को शक्ति प्रदान करती हैं।',
    ro: 'Outsourcing premium și inovație digitală din Casablanca. Echipe multilingve 24/7 care vă susțin creșterea.',
    cs: 'Prémiový outsourcing a digitální inovace z Casablanky. 24/7 vícejazyčné týmy podporující váš růst.',
    hu: 'Prémium outsourcing és digitális innováció Casablancából. 24/7 többnyelvű csapatok a növekedésért.',
    fi: 'Premium-ulkoistus ja digitaalinen innovaatio Casablancasta. 24/7 monikieliset tiimit kasvusi tukena.',
  }, lang),
  itDevelopment: tx({
    en: 'IT Development', fr: 'Développement IT', es: 'Desarrollo IT', ar: 'تطوير تكنولوجيا المعلومات',
    de: 'IT-Entwicklung', it: 'Sviluppo IT', pt: 'Desenvolvimento IT', nl: 'IT Ontwikkeling',
    pl: 'Rozwój IT', ru: 'Разработка ИТ', zh: 'IT开发', ja: 'IT開発',
    tr: 'IT Geliştirme', ko: 'IT 개발', sv: 'IT-utveckling', da: 'IT-udvikling',
    hi: 'IT विकास', ro: 'Dezvoltare IT', cs: 'IT vývoj', hu: 'IT fejlesztés', fi: 'IT-kehitys',
  }, lang),
  customSoftware: tx({
    en: 'Custom Enterprise Software', fr: 'Logiciel d\'Entreprise Sur Mesure', es: 'Software Empresarial a Medida', ar: 'برمجيات مؤسسية مخصصة',
    de: 'Individuelle Unternehmenssoftware', it: 'Software Aziendale Personalizzato', pt: 'Software Empresarial Personalizado', nl: 'Maatwerk Bedrijfssoftware',
    pl: 'Oprogramowanie Enterprise na Zamówienie', ru: 'Заказное Корпоративное ПО', zh: '定制企业软件', ja: 'カスタム企業ソフトウェア',
    tr: 'Özel Kurumsal Yazılım', ko: '맞춤형 기업 소프트웨어', sv: 'Skräddarsydd Företagsmjukvara', da: 'Skræddersyet Virksomhedssoftware',
    hi: 'कस्टम एंटरप्राइज़ सॉफ्टवेयर', ro: 'Software Enterprise Personalizat', cs: 'Zakázkový podnikový software', hu: 'Egyedi vállalati szoftver', fi: 'Räätälöity yritysohjelmisto',
  }, lang),
  staffAugmentation: tx({
    en: 'IT Staff Augmentation', fr: 'Augmentation d\'Équipe IT', es: 'Aumento de Personal IT', ar: 'تعزيز فرق تكنولوجيا المعلومات',
    de: 'IT-Personalverstärkung', it: 'Staff Augmentation IT', pt: 'Aumento de Equipa IT', nl: 'IT-Personeel Uitbreiden',
    pl: 'Wzmocnienie Zespołu IT', ru: 'Аутстаффинг ИТ', zh: 'IT人员扩充', ja: 'ITスタッフ増強',
    tr: 'IT Personel Takviyesi', ko: 'IT 인력 보강', sv: 'IT-personalförstärkning', da: 'IT-personaleudvidelse',
    hi: 'IT स्टाफ ऑगमेंटेशन', ro: 'Augmentare Personal IT', cs: 'Rozšíření IT týmu', hu: 'IT személyzet bővítés', fi: 'IT-henkilöstön vahvistaminen',
  }, lang),
  cloudDevOps: tx({
    en: 'Cloud & DevOps', fr: 'Cloud & DevOps', es: 'Cloud y DevOps', ar: 'السحابة والعمليات التطويرية',
    de: 'Cloud & DevOps', it: 'Cloud & DevOps', pt: 'Cloud & DevOps', nl: 'Cloud & DevOps',
    pl: 'Cloud & DevOps', ru: 'Облако и DevOps', zh: '云与DevOps', ja: 'クラウド＆DevOps',
    tr: 'Bulut & DevOps', ko: '클라우드 & DevOps', sv: 'Moln & DevOps', da: 'Cloud & DevOps',
    hi: 'क्लाउड और DevOps', ro: 'Cloud & DevOps', cs: 'Cloud & DevOps', hu: 'Cloud & DevOps', fi: 'Cloud & DevOps',
  }, lang),
  cybersecurity: tx({
    en: 'Cybersecurity', fr: 'Cybersécurité', es: 'Ciberseguridad', ar: 'الأمن السيبراني',
    de: 'Cybersicherheit', it: 'Cybersicurezza', pt: 'Cibersegurança', nl: 'Cybersecurity',
    pl: 'Cyberbezpieczeństwo', ru: 'Кибербезопасность', zh: '网络安全', ja: 'サイバーセキュリティ',
    tr: 'Siber Güvenlik', ko: '사이버 보안', sv: 'Cybersäkerhet', da: 'Cybersikkerhed',
    hi: 'साइबर सुरक्षा', ro: 'Securitate Cibernetică', cs: 'Kybernetická bezpečnost', hu: 'Kiberbiztonság', fi: 'Kyberturvallisuus',
  }, lang),
  mobileApps: tx({
    en: 'Mobile Apps', fr: 'Applications Mobiles', es: 'Apps Móviles', ar: 'تطبيقات الجوال',
    de: 'Mobile Apps', it: 'App Mobile', pt: 'Apps Móveis', nl: 'Mobiele Apps',
    pl: 'Aplikacje Mobilne', ru: 'Мобильные Приложения', zh: '移动应用', ja: 'モバイルアプリ',
    tr: 'Mobil Uygulamalar', ko: '모바일 앱', sv: 'Mobilappar', da: 'Mobilapps',
    hi: 'मोबाइल ऐप्स', ro: 'Aplicații Mobile', cs: 'Mobilní aplikace', hu: 'Mobilalkalmazások', fi: 'Mobiilisovellukset',
  }, lang),
  uiUxDesign: tx({
    en: 'UI/UX Design', fr: 'Design UI/UX', es: 'Diseño UI/UX', ar: 'تصميم واجهات المستخدم',
    de: 'UI/UX Design', it: 'Design UI/UX', pt: 'Design UI/UX', nl: 'UI/UX Design',
    pl: 'Projektowanie UI/UX', ru: 'UI/UX Дизайн', zh: 'UI/UX设计', ja: 'UI/UXデザイン',
    tr: 'UI/UX Tasarım', ko: 'UI/UX 디자인', sv: 'UI/UX-design', da: 'UI/UX Design',
    hi: 'UI/UX डिज़ाइन', ro: 'Design UI/UX', cs: 'UI/UX design', hu: 'UI/UX tervezés', fi: 'UI/UX-suunnittelu',
  }, lang),
  aiMlDev: tx({
    en: 'AI & Machine Learning', fr: 'IA & Machine Learning', es: 'IA & Machine Learning', ar: 'الذكاء الاصطناعي والتعلم الآلي',
    de: 'KI & Machine Learning', it: 'IA & Machine Learning', pt: 'IA & Machine Learning', nl: 'AI & Machine Learning',
    pl: 'AI & Machine Learning', ru: 'ИИ и Машинное Обучение', zh: 'AI与机器学习', ja: 'AI＆機械学習',
    tr: 'Yapay Zeka & Makine Öğrenimi', ko: 'AI & 머신러닝', sv: 'AI & Maskininlärning', da: 'AI & Machine Learning',
    hi: 'AI और मशीन लर्निंग', ro: 'AI & Machine Learning', cs: 'AI a strojové učení', hu: 'AI és gépi tanulás', fi: 'AI ja koneoppiminen',
  }, lang),
  ecommerce: tx({
    en: 'E-commerce Solutions', fr: 'Solutions E-commerce', es: 'Soluciones E-commerce', ar: 'حلول التجارة الإلكترونية',
    de: 'E-Commerce-Lösungen', it: 'Soluzioni E-commerce', pt: 'Soluções E-commerce', nl: 'E-commerce Oplossingen',
    pl: 'Rozwiązania E-commerce', ru: 'Решения для Электронной Коммерции', zh: '电子商务解决方案', ja: 'Eコマースソリューション',
    tr: 'E-ticaret Çözümleri', ko: '이커머스 솔루션', sv: 'E-handelslösningar', da: 'E-handelsløsninger',
    hi: 'ई-कॉमर्स समाधान', ro: 'Soluții E-commerce', cs: 'E-commerce řešení', hu: 'E-kereskedelmi megoldások', fi: 'Verkkokaupparatkaisut',
  }, lang),
  apiIntegration: tx({
    en: 'API & Integration', fr: 'API & Intégration', es: 'API e Integración', ar: 'واجهات التطبيقات والتكامل',
    de: 'API & Integration', it: 'API & Integrazione', pt: 'API & Integração', nl: 'API & Integratie',
    pl: 'API & Integracja', ru: 'API и Интеграция', zh: 'API与集成', ja: 'API＆インテグレーション',
    tr: 'API & Entegrasyon', ko: 'API & 통합', sv: 'API & Integration', da: 'API & Integration',
    hi: 'API और एकीकरण', ro: 'API și integrare', cs: 'API a integrace', hu: 'API és integráció', fi: 'API ja integraatio',
  }, lang),
  saas: tx({
    en: 'SaaS Development', fr: 'Développement SaaS', es: 'Desarrollo SaaS', ar: 'تطوير SaaS',
    de: 'SaaS Entwicklung', it: 'Sviluppo SaaS', pt: 'Desenvolvimento SaaS', nl: 'SaaS Ontwikkeling',
    pl: 'Rozwój SaaS', ru: 'Разработка SaaS', zh: 'SaaS开发', ja: 'SaaS開発',
    tr: 'SaaS Geliştirme', ko: 'SaaS 개발', sv: 'SaaS-utveckling', da: 'SaaS-udvikling',
    hi: 'SaaS विकास', ro: 'Dezvoltare SaaS', cs: 'Vývoj SaaS', hu: 'SaaS fejlesztés', fi: 'SaaS-kehitys',
  }, lang),
  callCenterBpo: tx({
    en: 'Call Center & BPO', fr: 'Centre d\'Appels & BPO', es: 'Contact Center & BPO', ar: 'مراكز الاتصال والتعهيد',
    de: 'Call Center & BPO', it: 'Call Center & BPO', pt: 'Call Center & BPO', nl: 'Callcenter & BPO',
    pl: 'Call Center & BPO', ru: 'Колл-центр & BPO', zh: '呼叫中心与BPO', ja: 'コールセンター＆BPO',
    tr: 'Çağrı Merkezi & BPO', ko: '콜센터 & BPO', sv: 'Callcenter & BPO', da: 'Callcenter & BPO',
    hi: 'कॉल सेंटर और BPO', ro: 'Call Center & BPO', cs: 'Call centrum & BPO', hu: 'Call Center & BPO', fi: 'Puhelinkeskus & BPO',
  }, lang),
  omnichannelSupport: tx({
    en: 'Omnichannel Support', fr: 'Support Omnicanal', es: 'Soporte Omnicanal', ar: 'الدعم متعدد القنوات',
    de: 'Omnichannel-Support', it: 'Supporto Omnichannel', pt: 'Suporte Omnichannel', nl: 'Omnichannel Support',
    pl: 'Wsparcie Omnikanałowe', ru: 'Омниканальная Поддержка', zh: '全渠道支持', ja: 'オムニチャネルサポート',
    tr: 'Çok Kanallı Destek', ko: '옴니채널 지원', sv: 'Omnikanalstöd', da: 'Omnichannel Support',
    hi: 'ऑम्नीचैनल सपोर्ट', ro: 'Suport Omnichannel', cs: 'Omnikanálová podpora', hu: 'Omnicsatornás támogatás', fi: 'Omnikanavainen tuki',
  }, lang),
  contentModeration: tx({
    en: 'Content Moderation', fr: 'Modération de Contenu', es: 'Moderación de Contenido', ar: 'إدارة المحتوى',
    de: 'Inhaltsmoderation', it: 'Moderazione Contenuti', pt: 'Moderação de Conteúdo', nl: 'Contentmoderatie',
    pl: 'Moderacja Treści', ru: 'Модерация Контента', zh: '内容审核', ja: 'コンテンツモデレーション',
    tr: 'İçerik Moderasyonu', ko: '콘텐츠 관리', sv: 'Innehållsmoderering', da: 'Indholdsmoderering',
    hi: 'सामग्री मॉडरेशन', ro: 'Moderare Conținut', cs: 'Moderace obsahu', hu: 'Tartalommoderálás', fi: 'Sisällön moderointi',
  }, lang),
  leadGeneration: tx({
    en: 'Lead Generation & Sales', fr: 'Génération de Leads & Ventes', es: 'Generación de Leads y Ventas', ar: 'توليد العملاء المحتملين والمبيعات',
    de: 'Leadgenerierung & Vertrieb', it: 'Lead Generation e Vendite', pt: 'Geração de Leads e Vendas', nl: 'Leadgeneratie & Verkoop',
    pl: 'Generowanie Leadów i Sprzedaż', ru: 'Генерация Лидов и Продажи', zh: '潜在客户开发与销售', ja: 'リード獲得＆セールス',
    tr: 'Potansiyel Müşteri Üretimi ve Satış', ko: '리드 생성 & 영업', sv: 'Leadgenerering & Försäljning', da: 'Leadgenerering & Salg',
    hi: 'लीड जनरेशन और बिक्री', ro: 'Generare de Lead-uri și Vânzări', cs: 'Generování leadů a prodej', hu: 'Leadgenerálás és értékesítés', fi: 'Liidien generointi ja myynti',
  }, lang),
  backOfficeDataEntry: tx({
    en: 'Back-Office & Data Entry', fr: 'Back-Office & Saisie de Données', es: 'Back-Office y Entrada de Datos', ar: 'المكتب الخلفي وإدخال البيانات',
    de: 'Backoffice & Datenerfassung', it: 'Back-Office & Data Entry', pt: 'Back-Office & Entrada de Dados', nl: 'Backoffice & Data Entry',
    pl: 'Back-Office & Wprowadzanie Danych', ru: 'Бэк-офис и Ввод Данных', zh: '后台与数据录入', ja: 'バックオフィス＆データ入力',
    tr: 'Arka Ofis & Veri Girişi', ko: '백오피스 & 데이터 입력', sv: 'Backoffice & Dataregistrering', da: 'Backoffice & Dataindtastning',
    hi: 'बैक-ऑफिस और डेटा एंट्री', ro: 'Back-Office și Introducere Date', cs: 'Back-office a zadávání dat', hu: 'Back-office és adatbevitel', fi: 'Back-office ja tietojen syöttö',
  }, lang),
  technicalSupport: tx({
    en: 'Technical Support', fr: 'Support Technique', es: 'Soporte Técnico', ar: 'الدعم الفني',
    de: 'Technischer Support', it: 'Supporto Tecnico', pt: 'Suporte Técnico', nl: 'Technische Ondersteuning',
    pl: 'Wsparcie Techniczne', ru: 'Техническая Поддержка', zh: '技术支持', ja: 'テクニカルサポート',
    tr: 'Teknik Destek', ko: '기술 지원', sv: 'Teknisk Support', da: 'Teknisk Support',
    hi: 'तकनीकी सहायता', ro: 'Suport Tehnic', cs: 'Technická podpora', hu: 'Műszaki támogatás', fi: 'Tekninen tuki',
  }, lang),
  customerRetention: tx({
    en: 'Customer Retention', fr: 'Fidélisation Client', es: 'Retención de Clientes', ar: 'الحفاظ على العملاء',
    de: 'Kundenbindung', it: 'Fidelizzazione Clienti', pt: 'Retenção de Clientes', nl: 'Klantbehoud',
    pl: 'Retencja Klientów', ru: 'Удержание Клиентов', zh: '客户留存', ja: '顧客維持',
    tr: 'Müşteri Tutma', ko: '고객 유지', sv: 'Kundbevarande', da: 'Kundefastholdelse',
    hi: 'ग्राहक प्रतिधारण', ro: 'Retenție Clienți', cs: 'Udržení zákazníků', hu: 'Ügyfélmegtartás', fi: 'Asiakaspysyvyys',
  }, lang),
  sdrAppointmentSetting: tx({
    en: 'SDR & Appointment Setting', fr: 'SDR & Prise de RDV', es: 'SDR & Concertación de Citas', ar: 'حجز المواعيد',
    de: 'SDR & Terminvereinbarung', it: 'SDR & Fissazione Appuntamenti', pt: 'SDR & Agendamento', nl: 'SDR & Afspraken Plannen',
    pl: 'SDR i Umawianie Spotkań', ru: 'SDR & Назначение Встреч', zh: 'SDR与预约安排', ja: 'SDR＆アポイント設定',
    tr: 'SDR & Randevu Ayarlama', ko: 'SDR & 미팅 세팅', sv: 'SDR & Mötesbokning', da: 'SDR & Mødebooking',
    hi: 'SDR और अपॉइंटमेंट सेटिंग', ro: 'SDR și Programare Întâlniri', cs: 'SDR a sjednávání schůzek', hu: 'SDR és találkozó szervezés', fi: 'SDR ja tapaamisten sopiminen',
  }, lang),
  multilingualSupport: tx({
    en: 'Multilingual Support', fr: 'Support Multilingue', es: 'Soporte Multilingüe', ar: 'الدعم متعدد اللغات',
    de: 'Mehrsprachiger Support', it: 'Supporto Multilingue', pt: 'Suporte Multilingue', nl: 'Meertalige Ondersteuning',
    pl: 'Wsparcie Wielojęzyczne', ru: 'Многоязычная Поддержка', zh: '多语言支持', ja: '多言語サポート',
    tr: 'Çok Dilli Destek', ko: '다국어 지원', sv: 'Flerspråkig Support', da: 'Flersproget Support',
    hi: 'बहुभाषी सहायता', ro: 'Suport Multilingv', cs: 'Vícejazyčná podpora', hu: 'Többnyelvű támogatás', fi: 'Monikielinen tuki',
  }, lang),
  liveChatMessaging: tx({
    en: 'Live Chat & Messaging', fr: 'Chat en Direct & Messagerie', es: 'Chat en Vivo y Mensajería', ar: 'الدردشة الحية والمراسلة',
    de: 'Live-Chat & Messaging', it: 'Live Chat & Messaggistica', pt: 'Chat ao Vivo & Mensagens', nl: 'Live Chat & Messaging',
    pl: 'Live Chat & Wiadomości', ru: 'Живой Чат и Мессенджеры', zh: '在线聊天与消息', ja: 'ライブチャット＆メッセージ',
    tr: 'Canlı Sohbet & Mesajlaşma', ko: '라이브 채팅 & 메시징', sv: 'Live Chat & Meddelanden', da: 'Live Chat & Beskeder',
    hi: 'लाइव चैट और मैसेजिंग', ro: 'Chat Live și Mesagerie', cs: 'Live chat a zprávy', hu: 'Élő chat és üzenetküldés', fi: 'Live-chat ja viestintä',
  }, lang),
  industriesAndCompany: tx({
    en: 'Industries & Company', fr: 'Industries & Entreprise', es: 'Industrias & Empresa', ar: 'الصناعات والشركة',
    de: 'Branchen & Unternehmen', it: 'Settori & Azienda', pt: 'Indústrias & Empresa', nl: 'Sectoren & Bedrijf',
    pl: 'Branże & Firma', ru: 'Отрасли & Компания', zh: '行业与公司', ja: '業界＆企業',
    tr: 'Sektörler & Şirket', ko: '산업 & 회사', sv: 'Branscher & Företag', da: 'Brancher & Virksomhed',
    hi: 'उद्योग और कंपनी', ro: 'Industrii și Companie', cs: 'Odvětví a společnost', hu: 'Iparágak és cég', fi: 'Toimialat ja yritys',
  }, lang),
  healthcare: tx({
    en: 'Healthcare & Pharma', fr: 'Santé & Pharma', es: 'Salud & Farmacéutica', ar: 'الرعاية الصحية والأدوية',
    de: 'Gesundheitswesen & Pharma', it: 'Sanità & Farmaceutica', pt: 'Saúde & Farmacêutica', nl: 'Zorg & Farma',
    pl: 'Ochrona Zdrowia & Farma', ru: 'Здравоохранение & Фарма', zh: '医疗与制药', ja: 'ヘルスケア＆製薬',
    tr: 'Sağlık & İlaç', ko: '헬스케어 & 제약', sv: 'Hälso- & Läkemedelsvård', da: 'Sundhed & Farmaci',
    hi: 'स्वास्थ्य सेवा और फार्मा', ro: 'Sănătate și Farma', cs: 'Zdravotnictví a farma', hu: 'Egészségügy és gyógyszer', fi: 'Terveydenhuolto ja lääketeollisuus',
  }, lang),
  fintech: tx({
    en: 'FinTech & Banking', fr: 'FinTech & Banque', es: 'FinTech & Banca', ar: 'التكنولوجيا المالية والبنوك',
    de: 'FinTech & Banken', it: 'FinTech & Banca', pt: 'FinTech & Bancos', nl: 'FinTech & Banken',
    pl: 'FinTech & Bankowość', ru: 'FinTech & Банки', zh: '金融科技与银行', ja: 'フィンテック＆銀行',
    tr: 'FinTech & Bankacılık', ko: '핀테크 & 뱅킹', sv: 'FinTech & Bank', da: 'FinTech & Bank',
    hi: 'फिनटेक और बैंकिंग', ro: 'FinTech și Bănci', cs: 'FinTech a bankovnictví', hu: 'FinTech és bankolás', fi: 'FinTech ja pankkitoiminta',
  }, lang),
  ecommerceIndustry: tx({
    en: 'E-commerce & Retail', fr: 'E-commerce & Commerce', es: 'E-commerce & Retail', ar: 'التجارة الإلكترونية والتجزئة',
    de: 'E-Commerce & Einzelhandel', it: 'E-commerce & Retail', pt: 'E-commerce & Varejo', nl: 'E-commerce & Retail',
    pl: 'E-commerce & Handel', ru: 'Электронная Коммерция & Ритейл', zh: '电子商务与零售', ja: 'Eコマース＆リテール',
    tr: 'E-ticaret & Perakende', ko: '이커머스 & 리테일', sv: 'E-handel & Detaljhandel', da: 'E-handel & Detailhandel',
    hi: 'ई-कॉमर्स और रिटेल', ro: 'E-commerce și Retail', cs: 'E-commerce a maloobchod', hu: 'E-kereskedelem és kiskereskedelem', fi: 'Verkkokauppa ja vähittäiskauppa',
  }, lang),
  travelHospitality: tx({
    en: 'Travel & Hospitality', fr: 'Voyage & Hôtellerie', es: 'Viajes & Hostelería', ar: 'السفر والضيافة',
    de: 'Reise & Gastgewerbe', it: 'Viaggi & Hospitality', pt: 'Viagens & Hotelaria', nl: 'Reizen & Hospitality',
    pl: 'Podróże & Hotelarstwo', ru: 'Путешествия & Гостеприимство', zh: '旅游与酒店', ja: '旅行＆ホスピタリティ',
    tr: 'Seyahat & Konaklama', ko: '여행 & 호스피탈리티', sv: 'Resor & Besöksnäring', da: 'Rejser & Hospitality',
    hi: 'यात्रा और आतिथ्य', ro: 'Turism și Ospitalitate', cs: 'Cestování a pohostinství', hu: 'Utazás és vendéglátás', fi: 'Matkailu ja ravintola-ala',
  }, lang),
  energy: tx({
    en: 'Energy & Utilities', fr: 'Énergie & Utilités', es: 'Energía & Utilities', ar: 'الطاقة والمرافق',
    de: 'Energie & Versorgung', it: 'Energia & Utilities', pt: 'Energia & Utilities', nl: 'Energie & Utilities',
    pl: 'Energetyka & Utilities', ru: 'Энергетика & Коммунальные Услуги', zh: '能源与公共事业', ja: 'エネルギー＆ユーティリティ',
    tr: 'Enerji & Altyapı', ko: '에너지 & 유틸리티', sv: 'Energi & Infrastruktur', da: 'Energi & Forsyning',
    hi: 'ऊर्जा और उपयोगिताएं', ro: 'Energie și Utilități', cs: 'Energie a utility', hu: 'Energia és közművek', fi: 'Energia ja kunnallistekniikka',
  }, lang),
  logistics: tx({
    en: 'Logistics & Supply Chain', fr: 'Logistique & Chaîne d\'Approvisionnement', es: 'Logística & Cadena de Suministro', ar: 'الخدمات اللوجستية وسلسلة التوريد',
    de: 'Logistik & Lieferkette', it: 'Logistica & Supply Chain', pt: 'Logística & Cadeia de Suprimentos', nl: 'Logistiek & Supply Chain',
    pl: 'Logistyka & Łańcuch Dostaw', ru: 'Логистика & Цепочка Поставок', zh: '物流与供应链', ja: '物流＆サプライチェーン',
    tr: 'Lojistik & Tedarik Zinciri', ko: '물류 & 공급망', sv: 'Logistik & Leveranskedja', da: 'Logistik & Forsyningskæde',
    hi: 'लॉजिस्टिक्स और आपूर्ति श्रृंखला', ro: 'Logistică și Lanț de Aprovizionare', cs: 'Logistika a dodavatelský řetězec', hu: 'Logisztika és ellátási lánc', fi: 'Logistiikka ja toimitusketju',
  }, lang),
  aboutUs: tx({
    en: 'About Us', fr: 'À Propos', es: 'Sobre Nosotros', ar: 'من نحن',
    de: 'Über Uns', it: 'Chi Siamo', pt: 'Sobre Nós', nl: 'Over Ons',
    pl: 'O Nas', ru: 'О Нас', zh: '关于我们', ja: '会社概要',
    tr: 'Hakkımızda', ko: '소개', sv: 'Om Oss', da: 'Om Os',
    hi: 'हमारे बारे में', ro: 'Despre Noi', cs: 'O nás', hu: 'Rólunk', fi: 'Meistä',
  }, lang),
  contact: tx({
    en: 'Contact', fr: 'Contact', es: 'Contacto', ar: 'اتصل بنا',
    de: 'Kontakt', it: 'Contatti', pt: 'Contato', nl: 'Contact',
    pl: 'Kontakt', ru: 'Контакты', zh: '联系我们', ja: 'お問い合わせ',
    tr: 'İletişim', ko: '문의', sv: 'Kontakt', da: 'Kontakt',
    hi: 'संपर्क', ro: 'Contact', cs: 'Kontakt', hu: 'Kapcsolat', fi: 'Yhteystiedot',
  }, lang),
  careers: tx({
    en: 'Careers', fr: 'Carrières', es: 'Carreras', ar: 'الوظائف',
    de: 'Karriere', it: 'Carriere', pt: 'Carreiras', nl: 'Vacatures',
    pl: 'Kariera', ru: 'Карьера', zh: '职业', ja: 'キャリア',
    tr: 'Kariyer', ko: '채용', sv: 'Karriär', da: 'Karriere',
    hi: 'करियर', ro: 'Cariere', cs: 'Kariéra', hu: 'Karrier', fi: 'Ura',
  }, lang),
  caseStudies: tx({
    en: 'Case Studies', fr: 'Études de Cas', es: 'Casos de Éxito', ar: 'دراسات الحالة',
    de: 'Fallstudien', it: 'Casi Studio', pt: 'Estudos de Caso', nl: 'Casestudies',
    pl: 'Studia Przypadków', ru: 'Кейсы', zh: '案例研究', ja: 'ケーススタディ',
    tr: 'Vaka Çalışmaları', ko: '사례 연구', sv: 'Fallstudier', da: 'Casestudier',
    hi: 'केस स्टडीज़', ro: 'Studii de Caz', cs: 'Případové studie', hu: 'Esettanulmányok', fi: 'Tapaustutkimukset',
  }, lang),
  pricing: tx({
    en: 'Pricing', fr: 'Tarifs', es: 'Precios', ar: 'الأسعار',
    de: 'Preise', it: 'Prezzi', pt: 'Preços', nl: 'Prijzen',
    pl: 'Cennik', ru: 'Цены', zh: '价格', ja: '料金',
    tr: 'Fiyatlandırma', ko: '요금제', sv: 'Priser', da: 'Priser',
    hi: 'मूल्य निर्धारण', ro: 'Prețuri', cs: 'Ceník', hu: 'Árak', fi: 'Hinnoittelu',
  }, lang),
  insights: tx({
    en: 'Insights & Blog', fr: 'Blog & Analyses', es: 'Blog & Análisis', ar: 'الرؤى والمدونة',
    de: 'Blog & Insights', it: 'Blog & Insights', pt: 'Blog & Insights', nl: 'Blog & Inzichten',
    pl: 'Blog & Analizy', ru: 'Блог & Аналитика', zh: '博客与洞察', ja: 'ブログ＆インサイト',
    tr: 'Blog & İçgörüler', ko: '블로그 & 인사이트', sv: 'Blogg & Insikter', da: 'Blog & Indsigter',
    hi: 'अंतर्दृष्टि और ब्लॉग', ro: 'Blog și Analize', cs: 'Blog a analýzy', hu: 'Blog és elemzések', fi: 'Blogi ja näkemykset',
  }, lang),
  stayUpdated: tx({
    en: 'Stay Updated', fr: 'Restez Informé', es: 'Mantente Informado', ar: 'ابقَ على اطلاع',
    de: 'Bleiben Sie Informiert', it: 'Resta Aggiornato', pt: 'Fique Atualizado', nl: 'Blijf Op de Hoogte',
    pl: 'Bądź na Bieżąco', ru: 'Будьте в Курсе', zh: '保持更新', ja: '最新情報を取得',
    tr: 'Güncel Kalın', ko: '최신 소식 받기', sv: 'Håll Dig Uppdaterad', da: 'Hold Dig Opdateret',
    hi: 'अपडेट रहें', ro: 'Rămâneți la Curent', cs: 'Zůstaňte informováni', hu: 'Maradjon naprakész', fi: 'Pysy ajan tasalla',
  }, lang),
  newsletterDesc: tx({
    en: 'Get the latest insights on IT outsourcing, BPO trends, and digital innovation.',
    fr: 'Recevez les dernières tendances en externalisation IT, BPO et innovation digitale.',
    es: 'Recibe las últimas tendencias en outsourcing IT, BPO e innovación digital.',
    ar: 'احصل على أحدث الرؤى حول تعهيد تكنولوجيا المعلومات واتجاهات BPO والابتكار الرقمي.',
    de: 'Erhalten Sie die neuesten Einblicke in IT-Outsourcing, BPO-Trends und digitale Innovation.',
    it: 'Ricevi gli ultimi approfondimenti su outsourcing IT, tendenze BPO e innovazione digitale.',
    pt: 'Receba os últimos insights sobre outsourcing de TI, tendências BPO e inovação digital.',
    nl: 'Ontvang de nieuwste inzichten over IT-outsourcing, BPO-trends en digitale innovatie.',
    pl: 'Otrzymuj najnowsze informacje o outsourcingu IT, trendach BPO i innowacjach cyfrowych.',
    ru: 'Получайте последние аналитические материалы об ИТ-аутсорсинге, трендах BPO и цифровых инновациях.',
    zh: '获取IT外包、BPO趋势和数字创新的最新洞察。',
    ja: 'ITアウトソーシング、BPOトレンド、デジタルイノベーションに関する最新インサイトをお届けします。',
    tr: 'IT dış kaynak, BPO trendleri ve dijital inovasyon hakkında en son bilgileri alın.',
    ko: 'IT 아웃소싱, BPO 트렌드, 디지털 혁신에 대한 최신 인사이트를 받아보세요.',
    sv: 'Få de senaste insikterna om IT-outsourcing, BPO-trender och digital innovation.',
    da: 'Få de seneste indsigter om IT-outsourcing, BPO-tendenser og digital innovation.',
    hi: 'IT आउटसोर्सिंग, BPO ट्रेंड और डिजिटल इनोवेशन पर नवीनतम अंतर्दृष्टि प्राप्त करें।',
    ro: 'Primiți cele mai recente informații despre outsourcing IT, tendințe BPO și inovație digitală.',
    cs: 'Získejte nejnovější poznatky o IT outsourcingu, BPO trendech a digitální inovaci.',
    hu: 'Kapja meg a legújabb betekintéseket az IT outsourcingról, BPO trendekről és digitális innovációról.',
    fi: 'Saa viimeisimmät näkemykset IT-ulkoistuksesta, BPO-trendeistä ja digitaalisesta innovaatiosta.',
  }, lang),
  enterEmail: tx({
    en: 'Enter your email', fr: 'Votre email', es: 'Tu email', ar: 'بريدك الإلكتروني',
    de: 'Ihre E-Mail', it: 'La tua email', pt: 'Seu email', nl: 'Uw e-mail',
    pl: 'Twój email', ru: 'Ваш email', zh: '输入邮箱', ja: 'メールを入力',
    tr: 'E-posta adresiniz', ko: '이메일 입력', sv: 'Din e-post', da: 'Din e-mail',
    hi: 'अपना ईमेल दर्ज करें', ro: 'Introduceți emailul', cs: 'Zadejte e-mail', hu: 'Adja meg e-mail címét', fi: 'Syötä sähköpostisi',
  }, lang),
  subscribe: tx({
    en: 'Subscribe', fr: 'S\'abonner', es: 'Suscribirse', ar: 'اشترك',
    de: 'Abonnieren', it: 'Iscriviti', pt: 'Inscrever-se', nl: 'Abonneer',
    pl: 'Subskrybuj', ru: 'Подписаться', zh: '订阅', ja: '登録',
    tr: 'Abone Ol', ko: '구독', sv: 'Prenumerera', da: 'Abonner',
    hi: 'सदस्यता लें', ro: 'Abonați-vă', cs: 'Odebírat', hu: 'Feliratkozás', fi: 'Tilaa',
  }, lang),
  subscribed: tx({
    en: 'Subscribed!', fr: 'Inscrit !', es: '¡Suscrito!', ar: 'تم الاشتراك!',
    de: 'Abonniert!', it: 'Iscritto!', pt: 'Inscrito!', nl: 'Geabonneerd!',
    pl: 'Zapisano!', ru: 'Подписано!', zh: '已订阅！', ja: '登録完了！',
    tr: 'Abone olundu!', ko: '구독 완료!', sv: 'Prenumererad!', da: 'Abonneret!',
    hi: 'सदस्यता ली!', ro: 'Abonat!', cs: 'Odebíráno!', hu: 'Feliratkozva!', fi: 'Tilattu!',
  }, lang),
  privacyPolicy: tx({
    en: 'Privacy Policy', fr: 'Politique de Confidentialité', es: 'Política de Privacidad', ar: 'سياسة الخصوصية',
    de: 'Datenschutz', it: 'Privacy Policy', pt: 'Política de Privacidade', nl: 'Privacybeleid',
    pl: 'Polityka Prywatności', ru: 'Политика Конфиденциальности', zh: '隐私政策', ja: 'プライバシーポリシー',
    tr: 'Gizlilik Politikası', ko: '개인정보처리방침', sv: 'Integritetspolicy', da: 'Privatlivspolitik',
    hi: 'गोपनीयता नीति', ro: 'Politica de Confidențialitate', cs: 'Zásady ochrany osobních údajů', hu: 'Adatvédelmi irányelvek', fi: 'Tietosuojakäytäntö',
  }, lang),
  termsOfService: tx({
    en: 'Terms of Service', fr: 'Conditions d\'Utilisation', es: 'Términos de Servicio', ar: 'شروط الخدمة',
    de: 'Nutzungsbedingungen', it: 'Termini di Servizio', pt: 'Termos de Serviço', nl: 'Servicevoorwaarden',
    pl: 'Warunki Usługi', ru: 'Условия Использования', zh: '服务条款', ja: '利用規約',
    tr: 'Hizmet Şartları', ko: '서비스 이용약관', sv: 'Tjänstevillkor', da: 'Servicevilkår',
    hi: 'सेवा की शर्तें', ro: 'Termeni și Condiții', cs: 'Podmínky služby', hu: 'Szolgáltatási feltételek', fi: 'Palveluehdot',
  }, lang),
  cookiePolicy: tx({
    en: 'Cookie Policy', fr: 'Politique de Cookies', es: 'Política de Cookies', ar: 'سياسة ملفات تعريف الارتباط',
    de: 'Cookie-Richtlinie', it: 'Cookie Policy', pt: 'Política de Cookies', nl: 'Cookiebeleid',
    pl: 'Polityka Cookies', ru: 'Политика Cookies', zh: 'Cookie政策', ja: 'Cookieポリシー',
    tr: 'Çerez Politikası', ko: '쿠키 정책', sv: 'Cookiepolicy', da: 'Cookiepolitik',
    hi: 'कुकी नीति', ro: 'Politica de Cookie-uri', cs: 'Zásady cookies', hu: 'Cookie szabályzat', fi: 'Evästekäytäntö',
  }, lang),
  whatsappChat: tx({
    en: 'WhatsApp Chat', fr: 'Chat WhatsApp', es: 'Chat WhatsApp', ar: 'واتساب',
    de: 'WhatsApp Chat', it: 'Chat WhatsApp', pt: 'Chat WhatsApp', nl: 'WhatsApp Chat',
    pl: 'Czat WhatsApp', ru: 'Чат WhatsApp', zh: 'WhatsApp聊天', ja: 'WhatsAppチャット',
    tr: 'WhatsApp Sohbet', ko: 'WhatsApp 채팅', sv: 'WhatsApp Chatt', da: 'WhatsApp Chat',
    hi: 'WhatsApp चैट', ro: 'Chat WhatsApp', cs: 'WhatsApp chat', hu: 'WhatsApp csevegés', fi: 'WhatsApp-keskustelu',
  }, lang),
  getQuote: tx({
    en: 'Request a Quote', fr: 'Demander un devis', es: 'Solicitar presupuesto', ar: 'اطلب عرض سعر',
    de: 'Angebot anfordern', it: 'Richiedi un preventivo', pt: 'Solicitar orçamento', nl: 'Offerte aanvragen',
    pl: 'Zapytaj o wycenę', ru: 'Запросить расчёт', zh: '获取报价', ja: 'お見積もり',
    tr: 'Teklif isteyin', ko: '견적 요청', sv: 'Begär offert', da: 'Anmod om tilbud',
    hi: 'कोटेशन अनुरोध', ro: 'Solicitați o ofertă', cs: 'Vyžádat nabídku', hu: 'Ajánlatkérés', fi: 'Pyydä tarjous',
  }, lang),
});

/* ─── Social links ─── */
const socialLinks = [
  { name: 'Twitter', icon: Twitter, href: BRAND.social.twitter },
  { name: 'Facebook', icon: Facebook, href: BRAND.social.facebook },
  { name: 'Instagram', icon: Instagram, href: BRAND.social.instagram },
  { name: 'GitHub', icon: Globe, href: BRAND.social.github }
];

const FOOTER_LOCAL_BUSINESS_SCHEMAS = [
  {
    '@context': 'https://schema.org', '@type': 'LocalBusiness', '@id': 'https://bpo-contact.com/#office-casablanca',
    name: 'BPO Contact — Casablanca HQ', image: 'https://bpo-contact.com/og-image.webp',
    telephone: BRAND.phone.main, email: BRAND.email.contact,
    address: { '@type': 'PostalAddress', addressLocality: 'Casablanca', addressCountry: 'MA' },
    geo: { '@type': 'GeoCoordinates', latitude: 33.5731, longitude: -7.5898 },
    openingHours: 'Mo-Su 00:00-23:59', priceRange: '€€',
  },
];

/* ─── Trust badges (localized) ───
   Integrity policy: only claims we can substantiate. Uncertifiable claims removed
   (no attestations held). Single data-protection badge, neutral wording. */
const getCertifications = (lang: string) => {
  const dataProtectionLabel: Record<string, string> = {
    en: 'Data Protection', fr: 'Protection des données', es: 'Protección de datos', ar: 'حماية البيانات',
    de: 'Datenschutz', it: 'Protezione dati', pt: 'Proteção de dados', nl: 'Gegevensbescherming',
    pl: 'Ochrona danych', ru: 'Защита данных', zh: '数据保护', ja: 'データ保護',
    tr: 'Veri koruma', ko: '데이터 보호', sv: 'Dataskydd', da: 'Databeskyttelse',
    hi: 'डेटा सुरक्षा', ro: 'Protecția datelor', cs: 'Ochrana dat', hu: 'Adatvédelem', fi: 'Tietosuoja',
  };
  return [dataProtectionLabel[lang] || dataProtectionLabel.en];
};

/* ================================================================ */
/*  Component                                                        */
/* ================================================================ */
export const Footer = forwardRef<HTMLElement>(function Footer(_, ref) {
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const l = getFooterLabels(language);
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setTimeout(() => setIsSubscribed(false), 3000);
      setEmail('');
    }
  };

  const lp = (path: string) => `/${language}/${path}`;

  useSchemaDOM(FOOTER_LOCAL_BUSINESS_SCHEMAS, 'footer-localbusiness', []);

  return (
    <footer
      ref={ref}
      aria-label="Site footer"
      className="bg-[hsl(228_42%_7%)] text-foreground pt-16 pb-0"
      role="contentinfo"
      itemScope
      itemType="https://schema.org/WPFooter"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Newsletter Banner ── */}
        <div className="bg-gradient-to-r from-primary/20 via-accent/10 to-primary/20 border border-border rounded-2xl p-6 sm:p-8 mb-14">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-bold mb-1">{l.stayUpdated}</h3>
              <p className="text-foreground text-sm">{l.newsletterDesc}</p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3">
              <Input
                type="email"
                placeholder={l.enterEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-muted/50 border-border text-foreground placeholder:text-white/80 flex-1"
                required
              />
              <Button type="submit" className="bg-primary hover:bg-primary/90 text-primary-foreground px-5 shrink-0" disabled={isSubscribed}>
                {isSubscribed ? <><CheckCircle className="h-4 w-4 mr-1.5" />{l.subscribed}</> : <><Send className="h-4 w-4 mr-1.5" />{l.subscribe}</>}
              </Button>
            </form>
          </div>
        </div>

        {/* ── Main 6-Column Grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-14">

          {/* COL 1 — Services */}
          <nav aria-label="Services">
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
              <Code2 className="h-4 w-4 text-primary" />
              Services
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: l.callCenterBpo, path: 'services/bpo' },
                { label: l.itDevelopment, path: 'services/digital-studio' },
                { label: l.mobileApps, path: 'services/digital-studio/mobile-apps' },
                { label: l.aiMlDev, path: 'services/digital-studio/ai-ml-development' },
                { label: l.cloudDevOps, path: 'services/digital-studio/cloud-devops' },
                { label: l.cybersecurity, path: 'services/digital-studio/cybersecurity-appsec' },
                { label: l.pricing, path: 'pricing' }
              ].map(item => (
                <li key={item.path}><Link to={lp(item.path)} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </nav>

          {/* COL 2 — Solutions by Industry */}
          <nav aria-label="Industries">
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              {l.industriesAndCompany?.split('&')[0]?.trim() || 'Industries'}
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: l.healthcare || 'Healthcare', slug: 'healthcare-bpo' },
                { label: l.fintech || 'Finance', slug: 'financial-services-outsourcing' },
                { label: l.ecommerceIndustry || 'E-commerce', slug: 'ecommerce-support' },
                { label: 'Telecom', slug: 'telecom-bpo' },
                { label: 'Insurance', slug: 'insurance-outsourcing' },
                { label: l.travelHospitality || 'Travel', slug: 'travel-hospitality-support' },
                { label: 'Education', slug: 'education-edtech-services' },
                { label: l.logistics || 'Logistics', slug: 'logistics-supply-chain' },
                { label: 'Real Estate', slug: 'real-estate-virtual-assistants' },
                { label: 'Legal', slug: 'legal-process-outsourcing' }
              ].map(item => (
                <li key={item.slug}><Link to={lp(`industries/${item.slug}`)} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </nav>

          {/* COL 3 — Outsourcing Destinations */}
          <nav aria-label="Outsourcing Destinations">
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              Outsourcing
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: '🇲🇦 Morocco', slug: 'outsourcing-morocco' },
                { label: '🇵🇱 Poland', slug: 'outsourcing-poland' },
                { label: '🇷🇴 Romania', slug: 'outsourcing-romania' },
                { label: '🇵🇹 Portugal', slug: 'outsourcing-portugal' },
                { label: '🇹🇷 Turkey', slug: 'outsourcing-turkey' },
                { label: '🇪🇬 Egypt', slug: 'outsourcing-egypt' },
                { label: '🇹🇳 Tunisia', slug: 'outsourcing-tunisia' },
                { label: '🇵🇭 Philippines', slug: 'outsourcing-philippines' },
                { label: '🇮🇳 India', slug: 'outsourcing-india' },
                { label: '🇨🇴 Colombia', slug: 'outsourcing-colombia' }
              ].map(item => (
                <li key={item.slug}><Link to={lp(item.slug)} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </nav>

          {/* COL 4 — Resources */}
          <nav aria-label="Resources">
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-primary" />
              Resources
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: l.insights || 'Blog', path: 'blog' },
                { label: l.caseStudies || 'Case Studies', path: 'case-studies' },
                { label: tx({ en: 'Testimonials', fr: 'Témoignages', es: 'Testimonios', ar: 'شهادات العملاء', de: 'Kundenstimmen', it: 'Testimonianze', pt: 'Depoimentos', nl: 'Getuigenissen', pl: 'Referencje', ru: 'Отзывы', zh: '客户评价', ja: 'お客様の声', tr: 'Müşteri Görüşleri', ko: '고객 후기', sv: 'Kundröster', da: 'Kundeudtalelser', hi: 'प्रशंसापत्र', ro: 'Testimoniale', cs: 'Reference', hu: 'Referenciák', fi: 'Asiakaskokemukset' }, language), path: 'testimonials' },
                { label: 'FAQ', path: 'faq' },
                { label: tx({ en: 'Partners', fr: 'Partenaires', es: 'Socios', ar: 'الشركاء', de: 'Partner', it: 'Partner', pt: 'Parceiros', nl: 'Partners', pl: 'Partnerzy', ru: 'Партнёры', zh: '合作伙伴', ja: 'パートナー', tr: 'Ortaklar', ko: '파트너', sv: 'Partners', da: 'Partnere', hi: 'भागीदार', ro: 'Parteneri', cs: 'Partneři', hu: 'Partnerek', fi: 'Kumppanit' }, language), path: 'partners' },
                { label: tx({ en: 'Why Morocco', fr: 'Pourquoi le Maroc', es: '¿Por Qué Marruecos?', ar: 'لماذا المغرب', de: 'Warum Marokko', it: 'Perché il Marocco', pt: 'Porquê Marrocos', nl: 'Waarom Marokko', pl: 'Dlaczego Maroko', ru: 'Почему Марокко', zh: '为什么选择摩洛哥', ja: 'なぜモロッコ', tr: 'Neden Fas', ko: '왜 모로코', sv: 'Varför Marocko', da: 'Hvorfor Marokko', hi: 'मोरक्को क्यों', ro: 'De Ce Maroc', cs: 'Proč Maroko', hu: 'Miért Marokkó', fi: 'Miksi Marokko' }, language), path: 'why-morocco' },
                { label: tx({ en: 'Comparison Guides', fr: 'Guides Comparatifs', es: 'Guías Comparativas', ar: 'أدلة المقارنة', de: 'Vergleichsleitfäden', it: 'Guide Comparative', pt: 'Guias Comparativos', nl: 'Vergelijkingsgidsen', pl: 'Poradniki porównawcze', ru: 'Сравнительные руководства', zh: '比较指南', ja: '比較ガイド', tr: 'Karşılaştırma Rehberleri', ko: '비교 가이드', sv: 'Jämförelseguider', da: 'Sammenligningsguider', hi: 'तुलना गाइड', ro: 'Ghiduri Comparative', cs: 'Srovnávací průvodce', hu: 'Összehasonlító útmutatók', fi: 'Vertailuoppaat' }, language), path: 'compare' },
                { label: tx({ en: 'ROI Calculator', fr: 'Calculateur ROI', es: 'Calculadora ROI', ar: 'حاسبة العائد', de: 'ROI-Rechner', it: 'Calcolatore ROI', pt: 'Calculadora ROI', nl: 'ROI Calculator', pl: 'Kalkulator ROI', ru: 'Калькулятор ROI', zh: 'ROI计算器', ja: 'ROI計算機', tr: 'ROI Hesaplayıcı', ko: 'ROI 계산기', sv: 'ROI-kalkylator', da: 'ROI-beregner', hi: 'ROI कैलकुलेटर', ro: 'Calculator ROI', cs: 'ROI kalkulačka', hu: 'ROI kalkulátor', fi: 'ROI-laskuri' }, language), path: 'roi-calculator' },
                { label: 'Sitemap', path: 'sitemap' }
              ].map(item => (
                <li key={item.path}><Link to={lp(item.path)} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </nav>

          {/* COL 5 — Company */}
          <nav aria-label="Company">
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              {tx({ en: 'Company', fr: 'Entreprise', es: 'Empresa', ar: 'الشركة', de: 'Unternehmen', it: 'Azienda', pt: 'Empresa', nl: 'Bedrijf', pl: 'Firma', ru: 'Компания', zh: '公司', ja: '会社', tr: 'Şirket', ko: '회사', sv: 'Företag', da: 'Virksomhed', hi: 'कंपनी', ro: 'Companie', cs: 'Společnost', hu: 'Cég', fi: 'Yritys' }, language)}
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: l.aboutUs, path: 'about' },
                { label: tx({ en: 'Leadership', fr: 'Direction', es: 'Liderazgo', ar: 'القيادة', de: 'Führung', it: 'Leadership', pt: 'Liderança', nl: 'Leiderschap', pl: 'Kierownictwo', ru: 'Руководство', zh: '领导层', ja: 'リーダーシップ', tr: 'Liderlik', ko: '리더십', sv: 'Ledarskap', da: 'Ledelse', hi: 'नेतृत्व', ro: 'Conducere', cs: 'Vedení', hu: 'Vezetés', fi: 'Johtoryhmä' }, language), path: 'about/leadership' },
                { label: tx({ en: 'Trust Center', fr: 'Centre de Confiance', es: 'Centro de Confianza', ar: 'مركز الثقة', de: 'Vertrauenszentrum', it: 'Centro Fiducia', pt: 'Centro de Confiança', nl: 'Vertrouwenscentrum', pl: 'Centrum Zaufania', ru: 'Центр Доверия', zh: '信任中心', ja: 'トラストセンター', tr: 'Güven Merkezi', ko: '신뢰 센터', sv: 'Förtroendecenter', da: 'Tillidscenter', hi: 'विश्वास केंद्र', ro: 'Centru de Încredere', cs: 'Centrum důvěry', hu: 'Bizalmi Központ', fi: 'Luottamuskeskus' }, language), path: 'trust-center' },
                { label: tx({ en: 'ESG & Responsibility', fr: 'RSE & Responsabilité', es: 'ESG & Responsabilidad', ar: 'الحوكمة البيئية والاجتماعية', de: 'ESG & Verantwortung', it: 'ESG & Responsabilità', pt: 'ESG & Responsabilidade', nl: 'ESG & Verantwoordelijkheid', pl: 'ESG & Odpowiedzialność', ru: 'ESG & Ответственность', zh: 'ESG与责任', ja: 'ESG＆責任', tr: 'ESG & Sorumluluk', ko: 'ESG 및 책임', sv: 'ESG & Ansvar', da: 'ESG & Ansvar', hi: 'ESG और जिम्मेदारी', ro: 'ESG & Responsabilitate', cs: 'ESG & Odpovědnost', hu: 'ESG & Felelősség', fi: 'ESG & Vastuu' }, language), path: 'esg' },
                { label: l.careers, path: 'careers' },
                { label: l.contact, path: 'contact' },
                { label: l.privacyPolicy, path: 'legal/privacy' },
                { label: l.termsOfService, path: 'legal/terms' },
                { label: l.cookiePolicy, path: 'legal/cookies' }
              ].map(item => (
                <li key={item.path}><Link to={lp(item.path)} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </nav>

          {/* COL 6 — Connect */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-primary" />
              Connect
            </h4>

            {/* Social Icons */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-muted/30 hover:bg-primary/20 flex items-center justify-center text-white/80 hover:text-primary transition-all"
                  aria-label={s.name}
                >
                  <s.icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>

            {/* Office Contacts with structured address */}
            <address className="not-italic space-y-3 text-sm text-white/80">
              <div className="flex items-start gap-2" itemScope itemType="https://schema.org/PostalAddress">
                <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                <div>
                  <span className="text-white text-xs font-semibold block">🇲🇦 Casablanca HQ</span>
                  <span className="text-xs"><span itemProp="addressLocality">Casablanca</span>, <span itemProp="addressCountry">Morocco</span></span>
                  <a href={`tel:${BRAND.phone.main.replace(/\s/g, '')}`} className="hover:text-primary transition-colors text-xs block mt-0.5" itemProp="telephone">{BRAND.phone.main}</a>
                </div>
              </div>
              <a href={`mailto:${BRAND.email.contact}`} className="flex items-center gap-2 hover:text-primary transition-colors text-xs">
                <Mail className="h-3.5 w-3.5 text-primary shrink-0" />{BRAND.email.contact}
              </a>
              <a href={`https://wa.me/${BRAND.phone.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-green-400 transition-colors text-xs">
                <MessageCircle className="h-3.5 w-3.5 text-green-400 shrink-0" />WhatsApp
              </a>
            </address>

            {/* CTA */}
            <div className="mt-4">
              <Button asChild size="sm" className="w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90 font-semibold text-xs">
                <Link to={startHref(language, pathname, 'footer-quote')}>{l.getQuote}</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* ── LocalBusiness Schema injected in <head> via useSchemaDOM ── */}

        {/* ── Popular Searches & Top Destinations ── */}
        <div className="border-t border-border pt-6 pb-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70 mb-3">{tx({
                en: 'Popular Searches', fr: 'Recherches populaires', es: 'Búsquedas populares', ar: 'عمليات البحث الشائعة',
                de: 'Beliebte Suchen', it: 'Ricerche popolari', pt: 'Pesquisas populares', nl: 'Populaire zoekopdrachten',
              }, language)}</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Call Center Morocco', href: '/hire-call-center-agents-morocco' },
                  { label: 'French Call Center', href: '/french-speaking-call-center' },
                  { label: 'Outsource Web Dev', href: '/outsource-web-development-morocco' },
                  { label: 'React Developers', href: '/react-developers-for-hire' },
                  { label: 'BPO for Startups', href: '/bpo-services-for-startups' },
                  { label: 'AI Call Center', href: '/ai-powered-call-center' },
                  { label: tx({ en: '24/7 Support on request', fr: 'Support 24/7 sur demande', pt: 'Suporte 24/7 mediante pedido', zh: '可按需安排 24/7 服务', ja: '24時間対応はご要望に応じて', ko: '24/7 운영은 요청 시 협의' }, language), href: '/24-7-call-center-services' },
                  { label: 'Global BPO Europe', href: '/nearshore-outsourcing-europe' },
                  { label: 'Dedicated Dev Team', href: '/dedicated-development-team' },
                  { label: 'Multilingual Support', href: '/multilingual-customer-support' },
                  { label: 'Node.js Developers', href: '/nodejs-developers-for-hire' },
                  { label: 'Python Developers', href: '/python-developers-for-hire' },
                  { label: 'E-commerce Dev', href: '/ecommerce-development-services' },
                  { label: 'SEO Outsourcing', href: '/seo-services-outsourcing' },
                  { label: 'Digital Marketing Morocco', href: '/digital-marketing-agency-morocco' }
                ].map((s) => (
                  <Link key={s.href} to={lp(s.href.slice(1))} className="text-[10px] px-2 py-1 rounded bg-muted/30 text-white/80 hover:text-primary hover:bg-muted/50 transition-colors">
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70 mb-3">{tx({
                en: 'Top Destinations', fr: 'Destinations phares', es: 'Destinos principales', ar: 'أفضل الوجهات',
                de: 'Top-Standorte', it: 'Destinazioni principali', pt: 'Principais destinos', nl: 'Topbestemmingen',
              }, language)}</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '🇲🇦 Morocco', href: '/outsourcing/morocco' },
                  { label: '🇮🇳 India', href: '/outsourcing/india' },
                  { label: '🇵🇭 Philippines', href: '/outsourcing/philippines' },
                  { label: '🇹🇳 Tunisia', href: '/outsourcing/tunisia' },
                  { label: '🇪🇬 Egypt', href: '/outsourcing/egypt' },
                  { label: '🇻🇳 Vietnam', href: '/outsourcing/vietnam' },
                  { label: '🇵🇱 Poland', href: '/outsourcing/poland' },
                  { label: '🇷🇴 Romania', href: '/outsourcing/romania' },
                  { label: '🇲🇽 Mexico', href: '/outsourcing/mexico' },
                  { label: '🇧🇷 Brazil', href: '/outsourcing/brazil' },
                  { label: '🇨🇴 Colombia', href: '/outsourcing/colombia' },
                  { label: '🇸🇳 Senegal', href: '/outsourcing/senegal' },
                  { label: '🇺🇦 Ukraine', href: '/outsourcing/ukraine' },
                  { label: '🇦🇷 Argentina', href: '/outsourcing/argentina' },
                  { label: '🇿🇦 South Africa', href: '/outsourcing/south-africa' },
                  { label: '🇲🇾 Malaysia', href: '/outsourcing/malaysia' },
                  { label: '🇨🇷 Costa Rica', href: '/outsourcing/costa-rica' },
                  { label: '🇵🇹 Portugal', href: '/outsourcing/portugal' },
                  { label: '🇰🇪 Kenya', href: '/outsourcing/kenya' },
                  { label: '🇧🇩 Bangladesh', href: '/outsourcing/bangladesh' },
                  { label: '🇪🇸 Spain', href: '/outsourcing/spain' },
                  { label: '🇬🇧 UK', href: '/outsourcing/united-kingdom' },
                  { label: '🇫🇷 France', href: '/outsourcing/france' },
                  { label: '🇩🇪 Germany', href: '/outsourcing/germany' }
                ].map((d) => (
                  <Link key={d.href} to={lp(d.href.slice(1))} className="text-[10px] px-2 py-1 rounded bg-muted/30 text-white/80 hover:text-primary hover:bg-muted/50 transition-colors">
                    {d.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Internal Linking Widget ── */}
        <div className="border-t border-border pt-8 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <nav aria-label="Footer related services">
              <h4 className="font-heading font-semibold text-white mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
                <Code2 className="h-4 w-4 text-primary" />
                Quick Links
              </h4>
              <ul className="space-y-2 text-sm">
                {[
                  { label: 'Call Center Services', path: 'services/bpo' },
                  { label: 'Web Development', path: 'services/digital-studio' },
                  { label: 'AI & Automation', path: 'services/digital-studio/ai-ml-development' },
                  { label: 'Multilingual Support', path: 'services/bpo/multilingual-support' }
                ].map(item => (
                  <li key={item.path}><Link to={lp(item.path)} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
                ))}
              </ul>
            </nav>
            <nav aria-label="Top Destinations">
              <h4 className="font-heading font-semibold text-white mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Top Destinations
              </h4>
              <ul className="space-y-2 text-sm">
                {[
                  { label: '🇲🇦 Casablanca', path: 'call-center-casablanca' },
                  { label: '🇬🇧 London', path: 'call-center-london' },
                  { label: '🇺🇸 New York', path: 'call-center-new-york' },
                  { label: '🇫🇷 Paris', path: 'call-center-paris' },
                  { label: '🇸🇦 Riyadh', path: 'call-center-riyadh' },
                  { label: '🌍 All cities', path: 'cities' }
                ].map(item => (
                  <li key={item.path}><Link to={lp(item.path)} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
                ))}
              </ul>
            </nav>
            <nav aria-label="Service Languages">
              <h4 className="font-heading font-semibold text-white mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
                <Globe className="h-4 w-4 text-primary" />
                Services In Your Language
              </h4>
              <ul className="space-y-2 text-sm">
                {[
                  { label: '🇬🇧 English', href: '/en/services/bpo' },
                  { label: '🇫🇷 Français', href: '/fr/services-centre-appel' },
                  { label: '🇪🇸 Español', href: '/es/servicios-call-center' },
                  { label: '🇸🇦 العربية', href: '/ar/khidmat-markaz-ittisalat' },
                  { label: '🇩🇪 Deutsch', href: '/de/callcenter-dienstleistungen' },
                  { label: '🇵🇹 Português', href: '/pt/servicos-call-center' },
                  { label: '🇮🇹 Italiano', href: '/it/servizi-call-center' },
                  { label: '🇳🇱 Nederlands', href: '/nl/callcenter-diensten' },
                  { label: '🇵🇱 Polski', href: '/pl/uslugi-call-center' },
                  { label: '🇮🇳 हिन्दी', href: '/hi/call-center-seva' },
                  { label: '🇹🇷 Türkçe', href: '/tr/cagri-merkezi-hizmetleri' },
                  { label: '🇯🇵 日本語', href: '/ja/call-center-service' },
                  { label: '🇰🇷 한국어', href: '/ko/call-center-seobiseu' },
                  { label: '🇨🇳 中文', href: '/zh/hulljiao-zhongxin-fuwu' },
                  { label: '🇮🇩 Bahasa Indonesia', href: '/id/layanan-call-center' },
                  { label: '🇲🇾 Bahasa Melayu', href: '/ms/pusat-panggilan' },
                  { label: '🇹🇭 ภาษาไทย', href: '/th/borikar-call-center' },
                  { label: '🇻🇳 Tiếng Việt', href: '/vi/trung-tam-cuoc-goi' },
                  { label: '🇸🇪 Svenska', href: '/sv/callcenter-tjanster' },
                  { label: '🇷🇴 Română', href: '/ro/servicii-call-center' },
                  { label: '🇨🇿 Čeština', href: '/cs/sluzby-call-centra' },
                  { label: '🇩🇰 Dansk', href: '/da/callcenter-tjenester' },
                  { label: '🇫🇮 Suomi', href: '/fi/puhelinkeskuspalvelut' },
                  { label: '🇬🇷 Ελληνικά', href: '/el/ypiresies-tilekentrikou' },
                  { label: '🇭🇺 Magyar', href: '/hu/call-center-szolgaltatasok' },
                  { label: '🇰🇪 Kiswahili', href: '/sw/huduma-call-center' },
                  { label: '🇧🇩 বাংলা', href: '/bn/call-center-sheba' },
                  { label: '🇵🇭 Tagalog', href: '/tl/call-center-serbisyo' }
                ].map(item => (
                  <li key={item.href}><Link to={item.href} className="text-white/80 hover:text-primary transition-colors">{item.label}</Link></li>
                ))}
              </ul>
            </nav>
          </div>
        </div>


        <div className="border-t border-border pt-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-5">

            {/* Left — Copyright */}
            <div className="flex items-center gap-3 text-white/80 text-sm">
              <Link to={`/${language}`} aria-label="BPO Contact home"><Logo size="xs" variant="icon" /></Link>
              <span>© {new Date().getFullYear()} BPO Contact. {tx({
                en: 'All rights reserved.', fr: 'Tous droits réservés.', es: 'Todos los derechos reservados.', ar: 'جميع الحقوق محفوظة.',
                de: 'Alle Rechte vorbehalten.', it: 'Tutti i diritti riservati.', pt: 'Todos os direitos reservados.', nl: 'Alle rechten voorbehouden.',
                pl: 'Wszelkie prawa zastrzeżone.', ru: 'Все права защищены.', zh: '版权所有。', ja: '全著作権所有。',
                tr: 'Tüm hakları saklıdır.', ko: '모든 권리 보유.', sv: 'Alla rättigheter förbehållna.', da: 'Alle rettigheder forbeholdes.',
                hi: 'सर्वाधिकार सुरक्षित।', ro: 'Toate drepturile rezervate.', cs: 'Všechna práva vyhrazena.', hu: 'Minden jog fenntartva.', fi: 'Kaikki oikeudet pidätetään.',
              }, language)}</span>
            </div>

            {/* Center — Certifications */}
            <div className="flex flex-wrap justify-center items-center gap-3">
              {getCertifications(language).map((cert) => (
                <span
                  key={cert}
                  className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md text-white text-xs font-medium"
                >
                  <Shield className="h-3 w-3 text-primary" />
                  {cert}
                </span>
              ))}
            </div>

            {/* Right — Legal links */}
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to={lp('legal/privacy')} className="text-white/80 hover:text-primary transition-colors">{l.privacyPolicy}</Link>
              <Link to={lp('legal/terms')} className="text-white/80 hover:text-primary transition-colors">{l.termsOfService}</Link>
              <Link to={lp('legal/cookies')} className="text-white/80 hover:text-primary transition-colors">{l.cookiePolicy}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
